import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, PartyPopper, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser, refreshUser } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent multiple calls
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verifyPayment = async () => {
      try {
        const paymentId = searchParams.get('payment_id');

        // Verify the PayFast payment on the backend
        const response = await subscriptionAPI.verifyPayfastPayment(paymentId);
        
        if (response.data.success) {
          setSuccess(true);
          
          // Update local user state
          if (updateUser && user) {
            updateUser({
              ...user,
              is_subscribed: true,
              subscription_end_date: response.data.subscription_end_date
            });
          }
          
          // Also refresh user data from server
          if (refreshUser) {
            await refreshUser();
          }
          
          // Show single toast
          toast.success('Payment successful! Your subscription is now active.');
        } else {
          setError('Could not verify payment. Please contact support.');
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        // Even if verification call fails, try to set success since PayFast redirected us here
        setSuccess(true);
        
        // Update local user state optimistically
        if (updateUser && user) {
          updateUser({
            ...user,
            is_subscribed: true,
          });
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, []); // Run once on mount, uses ref guard

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">Verifying Payment...</h1>
          <p className="text-slate-400">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (error && !success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Issue</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/subscribe')}
              className="w-full bg-amber-500 hover:bg-amber-600"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          </div>
          <p className="text-sm text-slate-500 mt-6">
            If you were charged but see this error, please email support@careerstep.co.za
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-8 text-center relative">
            <div className="absolute top-4 right-4">
              <PartyPopper className="w-8 h-8 text-white/50" />
            </div>
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">Payment Successful!</h1>
            <p className="text-emerald-100">Your subscription is now active</p>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="bg-emerald-50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-emerald-700 font-medium">Plan</span>
                <span className="text-emerald-900 font-bold">30 Days Access</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-emerald-700 font-medium">Amount Paid</span>
                <span className="text-emerald-900 font-bold">R25.00</span>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Create Professional CVs</p>
                  <p className="text-sm text-slate-500">Build up to 6 CVs with AI assistance</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">Download PDFs</p>
                  <p className="text-sm text-slate-500">Unlimited downloads, no watermarks</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">AI Features</p>
                  <p className="text-sm text-slate-500">Mock interviews, cover letters & more</p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-slate-400 text-sm mt-6">
          A confirmation email has been sent to your registered email address.
        </p>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
